// projectWizard/projectFormModel.js
//
// The single source of truth for what a project form IS: its fields, their
// types, which step owns them, what makes them valid, and — critically — how
// to convert between the API's shape and the form's shape.
//
// Why this file exists separately from the UI:
//   * `updateProject` on the backend builds its SQL SET clause from
//     Object.keys(req.body). Sending a key that is not a real column would
//     produce broken SQL. So the outbound payload must be built from an
//     explicit allow-list, never by spreading whatever the form happens to
//     hold. FIELD_TYPES below IS that allow-list.
//   * Hydrating an existing project is the part that was never built. The
//     API returns snake_case, nulls for empty values, and array columns that
//     may arrive as real arrays OR as JSON strings depending on the driver
//     and column type. fromApiProject() normalises all of that in one place
//     so every step component can assume clean, correctly-typed values.

import { DEFAULT_PROJECT_FORM } from '../ProjectFormSections';

// Re-exported so callers never have to reach into the legacy module.
export { DEFAULT_PROJECT_FORM };

/**
 * Every persisted field, with the type the FORM should hold it as.
 * This doubles as the outbound allow-list — nothing not listed here is ever
 * sent to the API.
 */
export const FIELD_TYPES = {
  // Basic
  name: 'text', description: 'text', vision: 'text', project_summary: 'text',
  category: 'text', difficulty_level: 'text', estimated_duration: 'number',
  duration_unit: 'text', total_sprints: 'number', estimated_versions: 'number',
  tags: 'list', status: 'text',
  // Purpose — persisted via the ngb_projects.details JSONB overflow, no
  // column or migration required; flattened back by the API like every
  // other overflow field.
  problem_statement: 'text', business_value: 'text',
  student_benefits: 'text', career_benefits: 'text',
  third_party_integrations: 'list',
  // Learning
  learning_objectives: 'text', prerequisites: 'list', recommended_skills: 'list',
  expected_outcomes: 'text',
  // Technology
  tech_stack_id: 'raw', tech_stacks: 'list', languages: 'list', primary_language: 'text',
  framework: 'text', database_type: 'text', architecture: 'text',
  frontend_tech: 'list', backend_tech: 'list', database_tech: 'list',
  cloud_platforms: 'list', cicd_tools: 'list', ai_tools: 'list',
  // Timeline
  start_date: 'date', end_date: 'date', enrollment_opens: 'date',
  enrollment_closes: 'date', expected_completion: 'date',
  // Team
  default_team_size: 'number', min_team_size: 'number', max_team_size: 'number',
  mentor_capacity: 'number', max_concurrent_teams: 'number',
  // Repository
  repository_url: 'text', github_organization: 'text', default_branch: 'text',
  branch_strategy: 'text', github_visibility: 'text', repository_template: 'bool',
  // Evaluation
  passing_score: 'number', min_pr_approval: 'number', min_ai_review_score: 'number',
  code_coverage_requirement: 'number', mandatory_ai_review: 'bool', mandatory_peer_review: 'bool',
  // Deliverables
  deliverables: 'list',
  // Certificates
  completion_certificate: 'bool', excellence_badge: 'bool',
  industry_recommendation: 'bool', linkedin_badge: 'bool',
  // Enrollment
  enrollment_type: 'text', preferred_role_selection: 'bool',
  preferred_roles: 'list', allow_multiple_roles: 'bool',
  // Assets
  logo_url: 'text', thumbnail_url: 'text', banner_url: 'text',
  architecture_diagram_url: 'text', demo_video_url: 'text',
  documentation_url: 'text', community_link: 'text',
};

export const ALL_FIELDS = Object.keys(FIELD_TYPES);

/**
 * Step definitions. `fields` are shown by default; `advanced` are the
 * pre-existing fields the redesign brief did not name — they stay fully
 * supported and editable, collapsed behind an "Advanced" disclosure so the
 * default view stays calm. Nothing is dropped.
 */
export const WIZARD_STEPS = [
  { id: 'basic', label: 'Basic Information', description: 'Name the project and describe what it is.',
    fields: ['name', 'project_summary', 'description', 'vision', 'tags', 'status'],
    advanced: ['category', 'estimated_versions'], required: ['name'] },
  { id: 'purpose', label: 'Purpose', description: 'Why this project exists and what students get out of it.',
    fields: ['problem_statement', 'business_value', 'student_benefits', 'career_benefits'],
    advanced: ['third_party_integrations'], required: [] },
  { id: 'learning', label: 'Learning', description: 'What students will learn and what they need first.',
    fields: ['learning_objectives', 'prerequisites', 'expected_outcomes', 'preferred_roles', 'difficulty_level', 'estimated_duration'],
    advanced: ['recommended_skills', 'duration_unit', 'total_sprints'], required: [] },
  { id: 'technology', label: 'Technology', description: 'The stack teams will build with.',
    fields: ['tech_stacks', 'languages', 'framework', 'architecture', 'database_type', 'cloud_platforms', 'cicd_tools', 'ai_tools', 'repository_url'],
    advanced: ['tech_stack_id', 'primary_language', 'frontend_tech', 'backend_tech', 'database_tech', 'github_organization', 'default_branch', 'branch_strategy', 'github_visibility', 'repository_template'],
    required: [] },
  { id: 'timeline', label: 'Timeline', description: 'When the project runs and when students can join.',
    fields: ['start_date', 'end_date', 'enrollment_opens', 'enrollment_closes'],
    advanced: ['expected_completion'], required: [] },
  { id: 'team', label: 'Team', description: 'How teams are sized and supported.',
    fields: ['default_team_size', 'mentor_capacity', 'max_concurrent_teams'],
    advanced: ['min_team_size', 'max_team_size', 'enrollment_type', 'preferred_role_selection', 'allow_multiple_roles'],
    required: [] },
  { id: 'evaluation', label: 'Evaluation', description: 'The bar a team must clear to pass.',
    fields: ['passing_score', 'min_ai_review_score', 'min_pr_approval', 'code_coverage_requirement', 'mandatory_ai_review', 'mandatory_peer_review'],
    advanced: [], required: [] },
  { id: 'deliverables', label: 'Deliverables', description: 'What each team must hand in.',
    fields: ['deliverables'], advanced: [], required: [] },
  { id: 'certificates', label: 'Certificates', description: 'What students earn on completion.',
    fields: ['completion_certificate', 'excellence_badge', 'industry_recommendation', 'linkedin_badge'],
    advanced: [], required: [] },
  { id: 'assets', label: 'Assets', description: 'Images, docs and links for the project page.',
    fields: ['logo_url', 'thumbnail_url', 'banner_url', 'documentation_url', 'demo_video_url', 'community_link'],
    advanced: ['architecture_diagram_url'], required: [] },
];

export const DELIVERABLE_OPTIONS = [
  { value: 'architecture_diagram', label: 'Architecture Diagram', hint: 'System design of the solution' },
  { value: 'presentation', label: 'Presentation', hint: 'Final slide deck' },
  { value: 'documentation', label: 'Documentation', hint: 'Technical and user docs' },
  { value: 'readme', label: 'README', hint: 'Repository front page' },
  { value: 'unit_tests', label: 'Unit Tests', hint: 'Automated test coverage' },
  { value: 'deployment', label: 'Deployment', hint: 'A running, reachable build' },
  { value: 'demo_video', label: 'Demo Video', hint: 'Recorded walkthrough' },
  { value: 'retrospective', label: 'Retrospective', hint: 'What the team learned' },
];

export const CERTIFICATE_OPTIONS = [
  { field: 'completion_certificate', label: 'Completion', hint: 'Awarded to everyone who finishes' },
  { field: 'excellence_badge', label: 'Excellence', hint: 'For teams scoring above the bar' },
  { field: 'industry_recommendation', label: 'Industry Recommendation', hint: 'A referable endorsement' },
  { field: 'linkedin_badge', label: 'LinkedIn Badge', hint: 'Shareable to their profile' },
];

// ── Normalisation ───────────────────────────────────────────────────────────

/**
 * Coerce anything the API might return for a list column into a real array.
 * Postgres array/JSON columns can arrive as a JS array, a JSON string, a
 * comma-joined string, or null depending on driver and column type — and a
 * string reaching a `.map()` in the UI is a render crash.
 */
export const toList = (value) => {
  if (Array.isArray(value)) return value.filter((v) => v !== null && v !== undefined && v !== '');
  if (value === null || value === undefined || value === '') return [];
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return parsed.filter((v) => v !== null && v !== undefined && v !== '');
      } catch (e) {
        /* fall through to comma split */
      }
    }
    return trimmed.split(',').map((s) => s.trim()).filter(Boolean);
  }
  return [value];
};

const toDateInput = (value) => {
  if (!value) return '';
  // <input type="date"> requires exactly yyyy-MM-dd. The API returns full
  // ISO timestamps, which the control silently rejects — leaving the field
  // blank and making a populated project look empty.
  const s = String(value);
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(s);
  if (m) return m[1];
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const toBool = (value) => value === true || value === 'true' || value === 1 || value === '1';

const toNumberInput = (value) =>
  value === null || value === undefined || value === '' ? '' : String(value);

/**
 * API project -> form state. This is the function the missing Edit feature
 * needed. Every key in FIELD_TYPES is populated, so no field is ever left
 * undefined (which would make a controlled input flip to uncontrolled and
 * warn/misbehave).
 */
/**
 * Fields without a column live in ngb_projects.details. The API is supposed to
 * flatten them into the project object, but this reads them itself as well.
 *
 * Depending on the API to have done it makes every overflow field — dates,
 * vision, objectives, the whole timeline step — invisible if that one step is
 * missing, out of date, or hands `details` back as a JSON string rather than an
 * object. There is no error in that case, the fields are just silently empty,
 * which is exactly what a start_date sitting in the database looked like.
 *
 * Flat keys win, so a real column always beats a stale copy in details.
 */
export const mergeProjectDetails = (row) => {
  if (!row || typeof row !== 'object') return {};
  const { details, ...flat } = row;

  let overflow = details;
  if (typeof overflow === 'string') {
    try { overflow = JSON.parse(overflow); } catch (e) { overflow = null; }
  }
  if (!overflow || typeof overflow !== 'object' || Array.isArray(overflow)) return flat;

  // A flat (column) value wins ONLY when it actually holds something. The
  // previous unconditional spread let a NULL column shadow the real value in
  // details — the exact bug that showed "Not provided" for saved fields on
  // rows whose data still lives in the overflow.
  const merged = { ...overflow };
  Object.entries(flat).forEach(([key, value]) => {
    if (value !== null && value !== undefined) merged[key] = value;
    else if (!(key in merged)) merged[key] = value;
  });
  return merged;
};

export const fromApiProject = (project) => {
  // GET /projects/:id does NOT return a flat project — it returns
  // { project, versions } (projectController.getProjectById). Reading the
  // fields off that wrapper found nothing, so every key fell through to its
  // default and the form rendered completely blank on an existing project.
  // Unwrap when the envelope is present, and still accept a flat object so
  // this works against either shape (the list endpoint returns flat rows).
  const raw = project && typeof project === 'object' ? project : {};
  const envelope =
    raw.project && typeof raw.project === 'object' && !Array.isArray(raw.project)
      ? raw.project
      : raw;
  const src = mergeProjectDetails(envelope);
  const form = { ...DEFAULT_PROJECT_FORM };
  ALL_FIELDS.forEach((field) => {
    const raw = src[field];
    const type = FIELD_TYPES[field];
    if (raw === undefined) return; // keep the default
    switch (type) {
      case 'list': form[field] = toList(raw); break;
      case 'date': form[field] = toDateInput(raw); break;
      case 'bool': form[field] = toBool(raw); break;
      case 'number': form[field] = toNumberInput(raw); break;
      case 'raw': form[field] = raw === null ? '' : raw; break;
      default: form[field] = raw === null ? '' : String(raw);
    }
  });

  // tech_stacks and languages have no column of their own — they are entered
  // in the wizard and derived down into tech_stack_id and primary_language on
  // save. Rows written before the details JSONB column existed therefore have
  // no copy to read back, and step 3 opened with two empty fields on a project
  // that clearly had a stack. Rebuild them from the derived columns when the
  // stored originals are absent.
  if (Array.isArray(form.languages) && form.languages.length === 0 && src.primary_language) {
    form.languages = toList(src.primary_language);
  }
  if (Array.isArray(form.tech_stacks) && form.tech_stacks.length === 0 && src.tech_stack_name) {
    form.tech_stacks = toList(src.tech_stack_name);
  }

  return form;
};

/**
 * Form state -> API payload. Built from the allow-list, never by spreading
 * form state, because the backend turns payload keys directly into SQL
 * columns. Empty numbers become null rather than '' so numeric columns are
 * not handed a blank string.
 */
export const toApiPayload = (form, { techStacks = [] } = {}) => {
  const src = form && typeof form === 'object' ? form : {};
  const payload = {};
  ALL_FIELDS.forEach((field) => {
    const value = src[field];
    switch (FIELD_TYPES[field]) {
      case 'list': payload[field] = toList(value); break;
      case 'bool': payload[field] = toBool(value); break;
      case 'number': payload[field] = value === '' || value === null || value === undefined ? null : Number(value); break;
      case 'date': payload[field] = value ? value : null; break;
      default: payload[field] = value === undefined ? null : value;
    }
  });

  // Preserve the two derivations the existing create path performs, so a
  // project saved through the wizard is byte-compatible with one saved
  // through the old dialog.
  const matched = (Array.isArray(techStacks) ? techStacks : []).find((s) =>
    toList(src.tech_stacks).some(
      (sel) =>
        String(sel).toLowerCase().includes(String(s && s.name).toLowerCase()) ||
        String(s && s.name).toLowerCase().includes(String(sel).toLowerCase())
    )
  );
  payload.tech_stack_id = src.tech_stack_id || (matched && matched.id) || null;
  const languages = toList(src.languages);
  payload.primary_language = languages.length ? languages.join(', ') : src.primary_language || '';
  return payload;
};

// ── Validation & progress ───────────────────────────────────────────────────

export const FIELD_LABELS = {
  name: 'Project name', project_summary: 'Summary', description: 'Description', vision: 'Vision',
  problem_statement: 'Problem statement', business_value: 'Business value',
  student_benefits: 'Student benefits', career_benefits: 'Career benefits',
  third_party_integrations: 'Third-party integrations',
  tags: 'Tags', status: 'Status', learning_objectives: 'Objectives', prerequisites: 'Prerequisites',
  expected_outcomes: 'Expected outcomes', preferred_roles: 'Roles', difficulty_level: 'Difficulty',
  estimated_duration: 'Duration', tech_stacks: 'Tech stacks', languages: 'Languages',
  framework: 'Frameworks', architecture: 'Architecture', database_type: 'Database',
  cloud_platforms: 'Cloud', cicd_tools: 'DevOps', ai_tools: 'AI models', repository_url: 'Repository',
  start_date: 'Start date', end_date: 'End date', enrollment_opens: 'Enrollment opens',
  enrollment_closes: 'Enrollment closes', default_team_size: 'Team size',
  mentor_capacity: 'Mentor capacity', max_concurrent_teams: 'Concurrent teams',
  passing_score: 'Passing score', min_ai_review_score: 'AI review score',
  min_pr_approval: 'PR approvals', code_coverage_requirement: 'Coverage',
  deliverables: 'Deliverables', logo_url: 'Logo', thumbnail_url: 'Thumbnail',
  banner_url: 'Banner', documentation_url: 'Documentation', demo_video_url: 'Video',
  community_link: 'Community link',
};

const isEmpty = (value) =>
  value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0);

const isPercent = (v) => v === '' || (Number(v) >= 0 && Number(v) <= 100);

/**
 * Validate one step. Returns { field: message }. Deliberately permissive:
 * only genuinely blocking problems are reported, because the brief asks to
 * reduce cognitive load — not to nag.
 */
export const validateStep = (stepId, form) => {
  const errors = {};
  const step = WIZARD_STEPS.find((s) => s.id === stepId);
  if (!step) return errors;

  step.required.forEach((field) => {
    if (isEmpty(form[field])) errors[field] = `${FIELD_LABELS[field] || field} is required`;
  });

  if (stepId === 'basic' && form.name && String(form.name).trim().length < 3) {
    errors.name = 'Use at least 3 characters';
  }
  if (stepId === 'technology' && isEmpty(form.tech_stacks) && isEmpty(form.tech_stack_id)) {
    // Mirrors the existing create guard so the wizard cannot submit
    // something the old dialog would have rejected.
    errors.tech_stacks = 'Select at least one tech stack';
  }
  if (stepId === 'timeline' && form.start_date && form.end_date && form.end_date < form.start_date) {
    errors.end_date = 'End date cannot be before the start date';
  }
  if (stepId === 'timeline' && form.enrollment_opens && form.enrollment_closes && form.enrollment_closes < form.enrollment_opens) {
    errors.enrollment_closes = 'Enrollment must close after it opens';
  }
  if (stepId === 'evaluation') {
    if (!isPercent(form.passing_score)) errors.passing_score = 'Must be between 0 and 100';
    if (!isPercent(form.min_ai_review_score)) errors.min_ai_review_score = 'Must be between 0 and 100';
    if (!isPercent(form.code_coverage_requirement)) errors.code_coverage_requirement = 'Must be between 0 and 100';
  }
  return errors;
};

/** Blocking errors across the whole form, keyed by step id. */
export const validateAll = (form) => {
  const byStep = {};
  WIZARD_STEPS.forEach((step) => {
    const errs = validateStep(step.id, form);
    if (Object.keys(errs).length) byStep[step.id] = errs;
  });
  return byStep;
};

/** Share of visible (non-advanced) fields that have a value — drives the % badge. */
export const completionPercent = (form) => {
  const tracked = WIZARD_STEPS.reduce((acc, s) => acc.concat(s.fields), []);
  if (!tracked.length) return 0;
  const filled = tracked.filter((f) => {
    const v = form[f];
    if (FIELD_TYPES[f] === 'bool') return v === true;
    return !isEmpty(v);
  }).length;
  return Math.round((filled / tracked.length) * 100);
};

export default {
  FIELD_TYPES, ALL_FIELDS, WIZARD_STEPS, DELIVERABLE_OPTIONS, CERTIFICATE_OPTIONS,
  fromApiProject, toApiPayload, validateStep, validateAll, completionPercent, toList,
};
